import { api } from '../../lib/api';
import { graphMailToken } from '../../lib/auth';
import type { OnboardingTemplateFile } from '../../lib/types';

/**
 * A task email that carries a file.
 *
 * A mailto: link cannot attach anything, so when a task's library entry keeps
 * a file (migration 182) the composer saves the message as a draft in the
 * sender's own Drafts folder, through Microsoft Graph, with the files on it.
 *
 * NOT AN .eml DOWNLOAD, which is what this did first. The new Outlook for
 * Windows opens one as a new message and adds the sender's signature ABOVE
 * the text already in it, and nothing in the file can move it. A draft that
 * already exists gets no automatic signature, so the message stays at the
 * top and the sender adds theirs underneath. Nothing is sent from here — the
 * same reasons the composer never sends mail itself still hold.
 */

/** A File's bytes as base64, without the data: prefix. */
export function readAsBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });
}

/** Hands a blob to the browser as a download. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const templateFilePath = (f: OnboardingTemplateFile) =>
  `/sales/onboarding/template-files/${f.FileId}?download=1`;

/** Downloads one library attachment through the authenticated API. */
export async function downloadTemplateFile(f: OnboardingTemplateFile) {
  saveBlob(await api.getBlob(templateFilePath(f)), f.FileName);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/* Graph takes attachments inline only while the whole request stays under
   4 MB; beyond that it needs an upload session, which these blank forms have
   never come near. Base64 adds a third, so the files themselves stop at 3 MB. */
const INLINE_LIMIT = 3 * 1024 * 1024;

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The plain text as HTML paragraphs, so Outlook shows it in the sender's usual font. */
function asHtml(body: string): string {
  return body
    .split(/\r?\n\r?\n/)
    .map((para) => `<p>${escapeHtml(para).replace(/\r?\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Saves the email to the sender's Drafts and returns the link that opens it
 * in Outlook on the web. It also appears in Drafts in the Outlook app.
 */
export async function saveDraftToMailbox(
  to: string,
  subject: string,
  body: string,
  files: OnboardingTemplateFile[],
): Promise<string> {
  const total = files.reduce((n, f) => n + f.SizeBytes, 0);
  if (total > INLINE_LIMIT) {
    throw new Error('The attachments are over 3 MB together, which is more than a draft can be given in one go.');
  }

  const attachments = await Promise.all(
    files.map(async (f) => ({
      '@odata.type': '#microsoft.graph.fileAttachment',
      name: f.FileName,
      contentType: f.MimeType,
      contentBytes: bytesToBase64(new Uint8Array(await (await api.getBlob(templateFilePath(f))).arrayBuffer())),
    })),
  );

  const recipients = to
    .split(/[;,]/)
    .map((address) => address.trim())
    .filter(Boolean)
    .map((address) => ({ emailAddress: { address } }));

  const response = await fetch('https://graph.microsoft.com/v1.0/me/messages', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${await graphMailToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      subject,
      body: { contentType: 'HTML', content: asHtml(body) },
      toRecipients: recipients,
      attachments,
    }),
  });
  const payload = (await response.json().catch(() => null)) as
    | { webLink?: string; error?: { message?: string } }
    | null;
  if (!response.ok) {
    throw new Error(payload?.error?.message ?? `Outlook did not save the draft (${response.status}).`);
  }
  return payload?.webLink ?? 'https://outlook.office.com/mail/drafts';
}
